import { Link, NavLink, useNavigate } from 'react-router-dom';
import { CalendarDays, CalendarRange, Plus, Users, Settings as SettingsIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { clearSession, getUser } from '../lib/auth';
import { ToastContainer } from './Toast';

export function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const user = getUser();

  function logout() {
    clearSession();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-panel border-b border-border px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <Link to="/" className="text-lg font-bold text-accent">NextGear Bookings</Link>
        <div className="flex items-center gap-2">
          {user && (
            <span className="hidden sm:inline text-sm text-muted mr-2">{user.display_name}</span>
          )}
          <button onClick={logout} className="btn-ghost px-3 py-2 text-sm">Sign out</button>
        </div>
      </header>

      <main className="flex-1 px-4 py-4 pb-24 max-w-3xl mx-auto w-full">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 bg-panel border-t border-border z-10 safe-bottom">
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          <TabLink to="/" label="Today" icon={CalendarDays} end />
          <TabLink to="/week" label="Week" icon={CalendarRange} />
          <NewBookingTab />
          <TabLink to="/customers" label="Customers" icon={Users} />
          <TabLink to="/settings" label="Settings" icon={SettingsIcon} />
        </div>
      </nav>

      <ToastContainer />
    </div>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `py-2.5 flex flex-col items-center gap-1 font-semibold text-[11px] sm:text-xs transition ${
          isActive ? 'text-accent' : 'text-muted hover:text-text/80'
        }`
      }
    >
      <Icon size={20} strokeWidth={2.25} />
      <span>{label}</span>
    </NavLink>
  );
}

function NewBookingTab() {
  return (
    <NavLink
      to="/new-booking"
      className="py-2 flex items-center justify-center"
      aria-label="New booking"
    >
      <span className="w-12 h-12 rounded-full bg-accent text-bg flex items-center justify-center shadow-lg active:scale-95 transition">
        <Plus size={26} strokeWidth={3} />
      </span>
    </NavLink>
  );
}
